import { useEffect, useMemo, useRef, useState } from 'react';
import Item from '../Item';
import SearchTabs from '../SearchTabs';
import UserCard from '../UserCard';
import styles from './index.module.scss';
import buttonStyles from '../../../styles/buttons.module.scss';
import { BackButtonIcon } from '../../../icons/ChatIcons';
import { useSearch } from '../../../hooks/useSearch';
import { searchGenerators } from '../../../utils/searchGenerators';
import { searchUsers, getUserFollowing } from '../../../utils/getUsers';
import { processSearchResults } from '../../../utils/userSearch';
import useIslandUserId from '../../../hooks/useIslandUserId';

const ANIMATION_DURATION_MS = 300;
const SWIPE_THRESHOLD = 50;

export default function SearchView({
  showSearchView,
  onToggleSearchView,
  dragOffset,
  enableTransition,
  isAnimating,
  setDragOffset,
  setEnableTransition,
  isWeb,
  onWebNavigate,
  currentZoneName,
  resolveItemType,
  searchQuery,
}) {
  const searchContentRef = useRef(null);
  const itemsContainerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('generators');
  const [isScrolled, setIsScrolled] = useState(false);
  const [followingList, setFollowingList] = useState([]);
  const [loadingFollowing, setLoadingFollowing] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Start as visible
  const wasInvisibleRef = useRef(false); // Track if we left the view

  const userId = useIslandUserId(!isWeb);
  const currentUserNumericId = userId ? Number(userId.replace(/island-/, '')) : null;

  const generatorSearch = useSearch(searchGenerators);
  const userSearch = useSearch(searchUsers);

  const searches = {
    generators: generatorSearch,
    people: userSearch,
  };

  const currentSearch = searches[activeTab] || generatorSearch;
  
  const emptyMessage = activeTab === 'generators' ? 'No generators found' : 'No users found';

  const processed = activeTab === 'people' ? processSearchResults({ items: currentSearch.results }) : [];
  
  // Create a Set of followed user IDs for O(1) lookup instead of O(N) with .some()
  const followingSet = useMemo(
    () => new Set(followingList.map(u => u.user_id)),
    [followingList]
  );
  
  // Enrich search results with follow status by checking if user appears in current user's following list
  const users = processed
    .filter(user => user.user_id !== currentUserNumericId) // Don't show current user in search results
    .map(user => ({
      ...user,
      is_following: followingSet.has(user.user_id),
    }));

  // Called by UserCard after successful follow/unfollow to update local state
  const updateFollowingList = (userId, isFollowing) => {
    setFollowingList(prev => {
      if (isFollowing) {
        return [...prev, { user_id: userId }];
      } else {
        return prev.filter(u => u.user_id !== userId);
      }
    });
  };

  // Listen for visibility changes from native app
  useEffect(() => {
    const handleAppEvents = (event) => {
      if (event.detail?.action === 'visibilityStatus') {
        const visible = event.detail?.params?.isVisible || false;
        
        // Track when we become invisible (user left SearchView)
        if (!visible) {
          wasInvisibleRef.current = true;
        }
        
        setIsVisible(visible);
      }
    };

    window.addEventListener('appEvent', handleAppEvents);

    return () => {
      window.removeEventListener('appEvent', handleAppEvents);
    };
  }, []);

  // Load/reload following list for 'people' tab
  // Initial load: when switching to 'people' tab and list is empty
  // Reload: when user returns after visiting another screen (wasInvisibleRef.current = true)
  useEffect(() => {
    if (activeTab !== 'people' || !userId) return;
    
    // Load if:
    // 1. First time (list is empty) OR
    // 2. User returned after leaving view (potential changes in native Profile)
    const shouldLoad = followingList.length === 0 || (isVisible && wasInvisibleRef.current);
    
    if (shouldLoad) {
      if (wasInvisibleRef.current) {
        wasInvisibleRef.current = false; // Reset flag after refetch
      }
      
      setLoadingFollowing(true);
      getUserFollowing(`${userId}`)
        .then(following => {
          if (Array.isArray(following)) {
            setFollowingList(following);
          } else {
            console.error('Failed to load following list:', following?.error);
            setFollowingList([]);
          }
        })
        .finally(() => {
          setLoadingFollowing(false);
        });
    }
  }, [activeTab, userId, followingList.length, isVisible]);

  // Sync search query with parent
  useEffect(() => {
    currentSearch.setQuery(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeTab]);

  /**
   * Infinite scroll - load more results when scrolling to bottom
   * Also track scroll position for tab styling
   */
  useEffect(() => {
    if (!showSearchView) return;

    const searchContent = searchContentRef.current;
    if (!searchContent) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = searchContent;
      const scrollThreshold = 100; // Trigger when 100px from bottom

      // Update scroll state for tab styling
      setIsScrolled(scrollTop > 10);

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        if (currentSearch.hasMore && !currentSearch.loadingMore) {
          currentSearch.loadMore();
        }
      }
    };

    searchContent.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      searchContent.removeEventListener('scroll', handleScroll);
    };
  }, [showSearchView, activeTab, generatorSearch, userSearch]);

  /**
   * Manual touch event handlers for search view - swipe right to close
   */
  useEffect(() => {
    if (!showSearchView) return;

    const searchContent = searchContentRef.current;
    if (!searchContent) return;

    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isOnCarousel = false;
    let direction = null;

    const handleSearchTouchStart = (e) => {
      isOnCarousel = e.target.closest('[data-carousel]') !== null;
      if (isOnCarousel) return;
      if (isAnimating) return;

      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      currentX = startX;
      direction = null;
    };

    const handleSearchTouchMove = (e) => {
      if (isOnCarousel || isAnimating || !startX) return;

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = touchX - startX;
      const deltaY = touchY - startY;

      if (
        direction === null &&
        (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)
      ) {
        direction =
          Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }

      if (direction === 'vertical') return;

      // Only allow swipe right to close (positive deltaX)
      if (direction === 'horizontal' && deltaX > 0) {
        currentX = touchX;
        const clampedOffset = Math.max(0, Math.min(150, deltaX));
        setDragOffset(clampedOffset);
      }
    };

    const handleSearchTouchEnd = () => {
      if (isOnCarousel || !startX) return;

      if (direction === 'vertical') {
        startX = 0;
        direction = null;
        return;
      }

      const finalDeltaX = currentX - startX;

      // Swipe right to close search
      if (finalDeltaX >= SWIPE_THRESHOLD) {
        setEnableTransition(true);
        setDragOffset(0);
        onToggleSearchView();
      } else {
        // Snap back
        setDragOffset(0);
      }

      startX = 0;
      direction = null;
    };

    const handleSearchTouchCancel = () => {
      startX = 0;
      direction = null;
      setDragOffset(0);
    };

    searchContent.addEventListener('touchstart', handleSearchTouchStart, {
      passive: true,
    });
    searchContent.addEventListener('touchmove', handleSearchTouchMove, {
      passive: true,
    });
    searchContent.addEventListener('touchend', handleSearchTouchEnd, {
      passive: true,
    });
    searchContent.addEventListener('touchcancel', handleSearchTouchCancel, {
      passive: true,
    });

    return () => {
      searchContent.removeEventListener('touchstart', handleSearchTouchStart);
      searchContent.removeEventListener('touchmove', handleSearchTouchMove);
      searchContent.removeEventListener('touchend', handleSearchTouchEnd);
      searchContent.removeEventListener('touchcancel', handleSearchTouchCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearchView, isAnimating, onToggleSearchView]);

  if (isWeb) return null;

  return (
    <>
      <div
        ref={searchContentRef}
        className={styles.searchContent}
        style={{
          transform: showSearchView
            ? `translateX(${dragOffset}px)`
            : 'translateX(100vw)',
          transition: enableTransition
            ? `transform ${ANIMATION_DURATION_MS}ms ease-out`
            : 'none',
          pointerEvents: showSearchView ? 'auto' : 'none',
        }}
      >
      <div ref={itemsContainerRef} className={styles.items}>
        <SearchTabs 
          activeTab={activeTab} 
          onTabChange={setActiveTab}
          isScrolled={isScrolled}
        />
        
        {/* Loading state */}
        {(currentSearch.loading || (activeTab === 'people' && loadingFollowing)) && (
          <div className={styles.noResults}>
            <img
              src="https://img.buzzfeed.com/store-an-image-prod-us-east-1/-QqSfapKn.gif"
              alt="Loading..."
            />
          </div>
        )}

        {/* Empty state */}
        {!currentSearch.loading && !(activeTab === 'people' && loadingFollowing) && currentSearch.results.length === 0 && (
          <div className={styles.noResults}>
            {searchQuery.trim()
              ? emptyMessage
              : 'Start typing to search'}
          </div>
        )}

        {/* Generator Results */}
        {!currentSearch.loading && currentSearch.results.length > 0 && activeTab === 'generators' &&
          currentSearch.results.filter(Boolean).map((item, index) => (
            <Item
              key={`generator-${item.id || index}`}
              index={index}
              item={item}
              resolveItemType={resolveItemType}
              contentObject={item}
              isWeb={isWeb}
              onWebNavigate={onWebNavigate}
              currentZoneName={currentZoneName}
            />
          ))
        }

        {/* People Results */}
        {!currentSearch.loading && !loadingFollowing && currentSearch.results.length > 0 && activeTab === 'people' && (
          <div className={styles.userList}>
            {users.map((user, index) => (
              <UserCard 
                key={`user-${user.user_id || index}`}
                user={user}
                onFollowChange={updateFollowingList}
              />
            ))}
          </div>
        )}

        {/* Loading more indicator */}
        {currentSearch.loadingMore && (
          <div className={styles.loadingMore}>
            <img
              src="https://img.buzzfeed.com/store-an-image-prod-us-east-1/-QqSfapKn.gif"
              alt="Loading more..."
            />
          </div>
        )}
      </div>
    </div>
    <div 
      className={styles.searchBackButton}
      style={{
        transform: showSearchView ? `translateX(${dragOffset}px)` : 'translateX(100vw)',
        opacity: showSearchView ? 1 : 0,
        transition: enableTransition 
          ? `transform ${ANIMATION_DURATION_MS}ms ease-out, opacity ${ANIMATION_DURATION_MS}ms ease-out` 
          : 'none',
      }}
    >
      <button
        className={`${buttonStyles.yellowButton} ${buttonStyles.lgButton} ${buttonStyles.backButton}`}
        onClick={onToggleSearchView}
        type="button"
        aria-label="Close search"
      >
        <BackButtonIcon />
      </button>
    </div>
    </>
  );
}
